import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { X, Dumbbell, Clock, Target, Link2 } from 'lucide-react';
import { formatRecoveryDisplaySeconds } from '../../../utils/formatRecoveryTime';
import ImageWithPreview from '../../common/ImageWithPreview';
import { getWorkoutSetTypeBadgeClass, getWorkoutSetTypeLabel } from '../../../constants/workoutSetTypes';
import {
    formatTemplateRepsLine,
    formatTemplateWeightHint,
    inferTemplateRepsMode,
} from '../../../constants/templateRepsMode';
import { getWorkoutRepsModeLabel } from '../../../constants/workoutRepsMode';

export default function TemplatePreviewModal({ isOpen, template, onClose, overlayClassName = 'z-50' }) {
  const { isDark } = useTheme();

  if (!isOpen || !template) return null;

  const getDifficultyColor = (level) => {
    const colors = {
      beginner: 'text-green-600 dark:text-green-400',
      intermediate: 'text-yellow-600 dark:text-yellow-400',
      advanced: 'text-orange-600 dark:text-orange-400',
      expert: 'text-red-600 dark:text-red-400'
    };
    return colors[level] || 'text-gray-600';
  };

  const getGoalText = (goal) => {
    const goals = {
      strength: 'Forza',
      hypertrophy: 'Ipertrofia',
      endurance: 'Resistenza',
      flexibility: 'Flessibilità',
      general: 'Generale'
    };
    return goals[goal] || goal;
  };

  const getDifficultyText = (level) => {
    const levels = {
      beginner: 'Principiante',
      intermediate: 'Intermedio',
      advanced: 'Avanzato',
      expert: 'Esperto'
    };
    return levels[level] || level;
  };

  // Funzione per parsare le note e estrarre il testo trainer
  const parseExerciseNote = (note) => {
    if (!note) return null;
    
    // Se è una stringa che inizia con '{', prova a parsare come JSON
    if (typeof note === 'string' && note.trim().startsWith('{')) {
      try {
        const notesObj = JSON.parse(note);
        // Estrai trainer (priorità) o user/system se trainer non presente
        return notesObj.trainer || notesObj.user || notesObj.system || null;
      } catch (e) {
        // Se non è JSON valido, considera come stringa legacy
        return note.trim();
      }
    }
    
    // Se è già un oggetto (già parsato)
    if (typeof note === 'object' && note !== null) {
      return note.trainer || note.user || note.system || null;
    }
    
    // Note legacy: stringa semplice
    return note.trim() || null;
  };

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 ${overlayClassName}`}>
      <div className={`bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">
                👁️ {template.name}
              </h2>
              <p className="text-white/80 text-sm mt-1">
                Anteprima Template
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          {/* Info Template */}
          <div className="mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <p className="text-xs text-gray-500 dark:text-white/65 mb-1">Difficoltà</p>
                <p className={`font-semibold ${getDifficultyColor(template.difficulty_level)}`}>
                  {getDifficultyText(template.difficulty_level)}
                </p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <p className="text-xs text-gray-500 dark:text-white/65 mb-1">Obiettivo</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {getGoalText(template.target_goal)}
                </p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <p className="text-xs text-gray-500 dark:text-white/65 mb-1">Durata</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  ~{template.estimated_duration} min
                </p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <p className="text-xs text-gray-500 dark:text-white/65 mb-1">Esercizi</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {template.exercise_count || template.exercises?.length || 0}
                </p>
              </div>
            </div>

            {template.description && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                  Descrizione
                </h3>
                <p className="text-gray-600 dark:text-white/65 whitespace-pre-line">
                  {template.description}
                </p>
              </div>
            )}

            {template.category_name && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                   style={{ 
                     backgroundColor: (template.category_color || '#6366f1') + '20',
                     color: template.category_color || '#6366f1'
                   }}>
                {template.category_name}
              </div>
            )}
          </div>

          {/* Esercizi */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Esercizi ({template.exercises?.length || 0})
            </h3>
            
            {template.exercises && template.exercises.length > 0 ? (
              <div className="space-y-3">
                {template.exercises.map((exercise, index) => (
                  <div 
                    key={exercise.id}
                    className={`p-4 rounded-lg border ${
                      exercise.linked_to_previous
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-400 border-l-4 ml-4'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-white/10'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-medium text-gray-500 dark:text-white/65">
                            {index + 1}.
                          </span>
                          {/* Immagine esercizio con preview hover */}
                          {(exercise.immagine_nome || exercise.immagine_statica) ? (
                            <div className="w-10 h-10 rounded overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0">
                              <ImageWithPreview
                                src={exercise.immagine_nome ? `/uploads/images/${exercise.immagine_nome}` : exercise.immagine_statica}
                                alt={exercise.nome || exercise.exercise_name || 'Esercizio'}
                                maxHeight="h-10"
                                maxWidth="w-10"
                                previewSize="w-80"
                              />
                            </div>
                          ) : (
                            <div className="w-10 h-10 rounded bg-gray-200 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                              <span className="text-gray-400 text-xs">📷</span>
                            </div>
                          )}
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {exercise.nome || exercise.exercise_name || 'Esercizio'}
                          </h4>
                          {exercise.set_type && exercise.set_type !== 'normal' && exercise.set_type.trim() !== '' && (
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getWorkoutSetTypeBadgeClass(exercise.set_type)}`}>
                              {getWorkoutSetTypeLabel(exercise.set_type)}
                            </span>
                          )}
                          {(() => {
                            const repsMode = inferTemplateRepsMode(exercise);
                            if (repsMode === 'amrap' && exercise.set_type !== 'piramidale') {
                              return (
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
                                  A cedimento
                                </span>
                              );
                            }
                            return null;
                          })()}
                          {exercise.linked_to_previous === 1 && (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                              <Link2 className="h-3 w-3 inline mr-1" />
                              Collegato
                            </span>
                          )}
                        </div>
                        
                        {exercise.descrizione && (
                          <p className="text-sm text-gray-500 dark:text-white/65 mb-3">
                            {exercise.descrizione}
                          </p>
                        )}

                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                          <div className="flex items-center text-gray-600 dark:text-white/65">
                            <Target className="h-4 w-4 mr-1 shrink-0" />
                            <span className="font-medium text-gray-800 dark:text-white/90">
                              {formatTemplateRepsLine(exercise)}
                            </span>
                            {(() => {
                              const mode = inferTemplateRepsMode(exercise);
                              if (mode === 'range' && exercise.set_type !== 'piramidale') {
                                return (
                                  <span className="ml-2 text-xs text-gray-500 dark:text-white/55">
                                    ({getWorkoutRepsModeLabel(mode)})
                                  </span>
                                );
                              }
                              return null;
                            })()}
                          </div>

                          {formatTemplateWeightHint(exercise) && (
                            <div className="flex items-center text-gray-600 dark:text-white/65">
                              <Dumbbell className="h-4 w-4 mr-1" />
                              @ {formatTemplateWeightHint(exercise)}
                            </div>
                          )}
                          
                          <div className="flex items-center text-gray-600 dark:text-white/65">
                            <Clock className="h-4 w-4 mr-1" />
                            {formatRecoveryDisplaySeconds(exercise.tempo_recupero)} rec
                          </div>
                        </div>

                        {(() => {
                          // Parsa note JSON o stringa legacy per estrarre testo trainer
                          const trainerNote = parseExerciseNote(exercise.notes);
                          return trainerNote ? (
                            <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-sm text-gray-600 dark:text-white/65">
                              💡 {trainerNote}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 dark:text-white/65 py-8">
                Nessun esercizio nel template
              </p>
            )}
          </div>

          {/* Info Creazione */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-white/10">
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 dark:text-white/65">
              <div>
                <span className="font-medium">Creato da:</span> {template.created_by_name}
              </div>
              <div>
                <span className="font-medium">Data creazione:</span> {new Date(template.created_at).toLocaleDateString('it-IT')}
              </div>
              {template.times_used > 0 && (
                <div>
                  <span className="font-medium">Utilizzi:</span> {template.times_used} volte
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-white/10 px-6 py-4 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-white/85 hover:bg-gray-300 dark:hover:brightness-110 rounded-lg transition-colors"
            >
              Chiudi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}












